import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Switch, Route, Redirect } from 'react-router-dom'
import { State } from 'store'
import { History, Location } from 'history'
import { withFeatures } from 'features'
import { Container, FullWidthContainerBody } from 'uiComponents/settingsContainer'
import { SecondaryTabsRow } from 'uiComponents/navigation/secondaryTabsRow'
import { PageAddButton } from 'uiComponents/buttons/pageAddButton'
import { KebabMenu } from 'uiComponents/menus/kebabMenu'
import { TableToolbar } from 'uiComponents/table/tableToolbar'
import OrdersSectionTabs from 'orders/sectionTabs'
import ResellersPage from 'channels/resellersPage'
import RetailersPage from 'channels/retailersPage'
import GoogleTTDPage from 'channels/googleTTD'
import SearchField from 'uiComponents/search/searchWithButton'
import './styles.scss'
import { withNavigation } from 'hocs'
import { match as RouteMatch } from 'react-router-dom'
import { Navigation } from 'navigation'
import { CreateNewType, ChannelsSectionType } from './channelsService'
import { BookingCodesService } from 'venue/bookingCodes/bookingCodesService'
import { ArticleService } from 'admin/articleService'
import { useTranslation } from 'translations'

interface ChannelsPageProps {
    history: History
    accountSlug: string
    location: Location
    navigation: Navigation
    match: RouteMatch<{}>
    bookingCodesService: BookingCodesService
    articleService: ArticleService
    hasFeature: (feature: string, accountSlug: string) => boolean
}

function ChannelsPage(props: ChannelsPageProps) {
    const { t } = useTranslation()
    const isConvious = useSelector((state: State) => state.auth.user?.isAdmin || false)

    const [activeSection, setActiveSection] = useState<ChannelsSectionType>('resellers')
    const [creatingNew, setCreatingNew] = useState<CreateNewType>(null)
    const [showAddCampaign, setShowAddCampaign] = useState<boolean>(false)
    const [showAddVenue, setShowAddVenue] = useState<boolean>(false)

    const onSectionChange = (section: ChannelsSectionType) => {
        setActiveSection(section)
    }

    const showAddCampaignButton = (value: boolean) => {
        setShowAddCampaign(value)
    }

    const showAddVenueButton = (value: boolean) => {
        setShowAddVenue(value)
    }

    const resellersFeatureOn = props.hasFeature('ResellersFeature', props.accountSlug)
    const retailersFeatureOn = props.hasFeature('RetailersReservationCodes', props.accountSlug)
    const googleTTDFeatureOn = props.hasFeature('GoogleThingsToDo', props.accountSlug)
    const sectionsList: ChannelsSectionType[] = []
    if (resellersFeatureOn) {
        sectionsList.push('resellers')
    }
    if (retailersFeatureOn) {
        sectionsList.push('retailers')
    }
    if (googleTTDFeatureOn) {
        sectionsList.push('google_ttd')
    }
    const search = props.navigation.query().q

    const channelTabs = [
        { name: 'Resellers', section: 'resellers' as const, shouldShow: resellersFeatureOn },
        { name: 'Retailers', section: 'retailers' as const, shouldShow: retailersFeatureOn },
        { name: 'Google', section: 'google_ttd' as const, shouldShow: googleTTDFeatureOn },
    ]
        .filter((tab) => tab.shouldShow)
        .map((tab) => ({
            name: tab.name,
            current: activeSection === tab.section,
            href: `/account/${props.accountSlug}/orders/channels/${tab.section}/`,
            onClick: () => props.history.push(`/account/${props.accountSlug}/orders/channels/${tab.section}/`),
        }))

    const addActions = (
        <>
            {(resellersFeatureOn || retailersFeatureOn) &&
                activeSection !== 'google_ttd' &&
                (activeSection === 'retailers' && showAddCampaign ? (
                    <KebabMenu
                        listAnchor="right"
                        triggerLabel={t('channels.page.moreActions')}
                        options={[
                            {
                                label: t('channels.page.addNewCampaign'),
                                onClick: () => setCreatingNew('campaign'),
                            },
                            {
                                label: t('channels.page.addNewRetailer'),
                                onClick: () => setCreatingNew(activeSection as CreateNewType),
                            },
                        ]}
                    />
                ) : (
                    <PageAddButton onClick={() => setCreatingNew(activeSection as CreateNewType)} id="add-new">
                        {t('channels.page.addNew')}
                    </PageAddButton>
                ))}
            {isConvious && activeSection === 'google_ttd' && googleTTDFeatureOn && showAddVenue && (
                <PageAddButton onClick={() => setCreatingNew(activeSection as CreateNewType)} id="add-venue">
                    {t('channels.page.addVenue')}
                </PageAddButton>
            )}
        </>
    )

    return (
        <div style={{ position: 'relative' }} id="channels">
            <OrdersSectionTabs />
            <SecondaryTabsRow tabs={channelTabs} actions={addActions} />
            <Container>
                {['resellers', 'retailers'].includes(activeSection) && (
                    <TableToolbar>
                        <SearchField />
                    </TableToolbar>
                )}
                <Switch location={props.location}>
                    <Route
                        path="/account/:accountSlug/orders/channels/resellers/"
                        render={() => (
                            <FullWidthContainerBody>
                                <ResellersPage
                                    accountSlug={props.accountSlug}
                                    onSectionChange={onSectionChange}
                                    creatingModeOn={creatingNew === ('resellers' as CreateNewType)}
                                    resetCreatingMode={() => setCreatingNew(null)}
                                    search={search}
                                />
                            </FullWidthContainerBody>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/orders/channels/retailers/"
                        render={() => (
                            <FullWidthContainerBody>
                                <RetailersPage
                                    accountSlug={props.accountSlug}
                                    onSectionChange={onSectionChange}
                                    creatingNew={creatingNew}
                                    resetCreatingMode={() => setCreatingNew(null)}
                                    search={search}
                                    showAddCampaignButton={showAddCampaignButton}
                                    bookingCodesService={props.bookingCodesService}
                                />
                            </FullWidthContainerBody>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/orders/channels/google_ttd/"
                        render={() => (
                            <FullWidthContainerBody>
                                <GoogleTTDPage
                                    accountSlug={props.accountSlug}
                                    onSectionChange={onSectionChange}
                                    creatingModeOn={creatingNew === ('google_ttd' as CreateNewType)}
                                    resetCreatingMode={() => setCreatingNew(null)}
                                    showAddVenueButton={showAddVenueButton}
                                    articleService={props.articleService}
                                />
                            </FullWidthContainerBody>
                        )}
                    />
                    <Route
                        render={() => (
                            <Redirect to={`/account/${props.accountSlug}/orders/channels/${sectionsList[0]}/`} />
                        )}
                    />
                </Switch>
            </Container>
        </div>
    )
}

export default withFeatures(withNavigation(ChannelsPage))
